import { imageSrc } from '@/lib/images'
import type { Banner } from '@/types/radio'
import {ChevronLeft, ChevronRight} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

interface BannerCarouselProps {
  banners: Banner[]
  rotation?: string
}

function pickWeighted(items: Banner[], currentId?: string) {
  const pool = items.length > 1 ? items.filter((item) => (item._id || item.title) !== currentId) : items
  const total = pool.reduce((sum, item) => sum + Math.max(1, Number(item.weight || 1)), 0)
  let ticket = Math.random() * total
  for (const item of pool) {
    ticket -= Math.max(1, Number(item.weight || 1))
    if (ticket <= 0) return item
  }
  return pool[0]
}

function BannerCarousel({ banners, rotation = 'sequential' }: BannerCarouselProps) {
  const items = useMemo(
    () => (banners || []).filter((item) => item?.active).sort((a, b) => (a.order || 0) - (b.order || 0)),
    [banners],
  )
  const [current, setCurrent] = useState<Banner | null>(items[0] || null)
  const currentId = current?._id || current?.title
  const timerRef = useRef<number | null>(null)
  const isRandom = rotation === 'random'

  useEffect(() => {
    if (!items.length) {
      setCurrent(null)
      return
    }
    setCurrent((prev) => {
      const stillThere = prev ? items.find((item) => (item._id || item.title) === (prev._id || prev.title)) : null
      return stillThere || items[0]
    })
  }, [items])

  useEffect(() => {
    if (!current || items.length < 2) return undefined
    const duration = Math.max(3, Number(current.durationSeconds || 15)) * 1000
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      if (isRandom) {
        setCurrent(pickWeighted(items, currentId))
        return
      }
      const index = items.findIndex((item) => (item._id || item.title) === currentId)
      setCurrent(items[(index + 1) % items.length])
    }, duration)
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [current, currentId, isRandom, items])

  if (!items.length || !current) return null

  const prev = () => {
    const index = items.findIndex((item) => (item._id || item.title) === currentId)
    setCurrent(items[(index - 1 + items.length) % items.length])
  }
  const next = () => {
    if (isRandom) {
      setCurrent(pickWeighted(items, currentId))
      return
    }
    const index = items.findIndex((item) => (item._id || item.title) === currentId)
    setCurrent(items[(index + 1) % items.length])
  }

  const content = (
    <>
      <img
        src={imageSrc(current.imageUrl)}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-xl"
      />
      <div className="absolute inset-0 bg-[#071a63]/30" />
      <img
        src={imageSrc(current.imageUrl)}
        alt={current.title}
        className="absolute inset-0 h-full w-full object-contain"
      />
      <span className="absolute left-2 top-2 z-20 rounded-md bg-black/70 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.14em] text-white sm:left-5 sm:top-4 sm:px-2.5 sm:py-1 sm:text-[11px] sm:tracking-[0.18em]">
        Publicidade
      </span>
      {(current.title || current.subtitle) ? (
        <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 sm:px-6 sm:pb-3">
          {current.title ? (
            <h3 className="line-clamp-1 max-w-3xl font-display text-xs font-black uppercase leading-tight text-white drop-shadow sm:text-base lg:text-lg">
              {current.title}
            </h3>
          ) : null}
          {current.subtitle ? (
            <p className="mt-0.5 line-clamp-1 max-w-2xl text-[10px] text-white/90 sm:text-xs">{current.subtitle}</p>
          ) : null}
        </div>
      ) : null}
    </>
  )

  return (
    <section className="mx-auto mt-4 w-full max-w-7xl px-3 sm:px-4 lg:px-6">
      <div className="relative w-full overflow-hidden rounded-2xl sm:rounded-[28px]">
        <div className="relative aspect-[16/9] w-full sm:aspect-[21/9] lg:aspect-[3/1]">
          {current.linkUrl ? (
            <a href={current.linkUrl} className="absolute inset-0 block" target="_blank" rel="noreferrer">
              {content}
            </a>
          ) : (
            <div className="absolute inset-0">{content}</div>
          )}
        </div>

        {items.length > 1 ? (
          <>
            <button
              type="button"
              onClick={prev}
              className="absolute left-2 top-1/2 z-20 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white/95 text-[#0b2ea8] shadow sm:left-5 sm:h-10 sm:w-10"
              aria-label="Publicidade anterior"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={next}
              className="absolute right-2 top-1/2 z-20 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-white/95 text-[#0b2ea8] shadow sm:right-5 sm:h-10 sm:w-10"
              aria-label="Próxima publicidade"
            >
              <ChevronRight size={16} />
            </button>
          </>
        ) : null}
      </div>
    </section>
  )
}

export default BannerCarousel
